import { ChangeEvent, useEffect, useState } from 'react'
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { CircularProgress, Pagination, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sxPagination, sxSubtitle, sxTypographySubTitleBold, sxTypographySubTitle } from './sxProps';
import './styles.css'
import LoadingApp from '../../../components/LoadingApp';
import { getComprovanteRecebidos } from '../../../services/FinanceiroService'
import { base64toBlob } from '../../../utils/Base64ToBlob';
import { Titulo } from '../../../interfaces/entities/Titulo';
import TabelaComprovantes from './TabelaComprovantes';
import ErrorModal from './ErrorModal';
import { ComprovanteRecebido } from '../../../interfaces/entities/ComprovanteRecebido';
import { faArrowDown } from "@fortawesome/pro-solid-svg-icons";
import { faFileInvoiceDollar } from '@fortawesome/pro-duotone-svg-icons';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import { sxPaginationContrast } from '../../../assets/styles/CommonSxProps';
import { scrollTo } from '../../../utils/ScrollUtil';
import AudioLoadingDescription from '../../../components/Accessibility/AudioLoadingDescription';
import CircularProgressLoading from '../../../components/Accessibility/CircularProgressLoading';
import PageTitle from '../../../components/Accessibility/PageTitle';

function ComprovanteRecebidos() {

  const [comprovantes, setComprovantes] = useState<ComprovanteRecebido[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loadingComprovantes, setLoadingComprovantes] = useState<boolean>(true);
  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const paginationLength = 5;

  const accessibility = useAccessibility();

  useEffect(() => {
    setLoadingComprovantes(true);
    getComprovanteRecebidos()
      .then(response => {
        setComprovantes(response);
      })
      .catch(error => {
        const message = error?.data?.message;
        setErrorMessage(message ? message : '');
        setErrorModal(true);
      })
      .finally(() => {
        setLoadingComprovantes(false);
      })
  }, []);

  const handlePageChange = (event: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    scrollTo('scroll-ref');
  };

  const onVisualizarClicked = (pdf: string) => {
    const blob = base64toBlob(pdf.split(',')[1]);
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl);
  }

  const handleCloseErrorModal = () => {
    setErrorModal(false);
  }

  const paginatedComprovantes = comprovantes
    .slice(1)
    .slice(
      (page - 1) * paginationLength,
      page * paginationLength
    );

  return (
    <div className='container' aria-atomic>
      <AudioLoadingDescription loading={loadingComprovantes} />
      <PageTitle text="Comprovante de pagamentos recebidos" />
      <ErrorModal open={errorModal} errorMessage={errorMessage} handleCloseModal={handleCloseErrorModal} />
      {loadingComprovantes &&
        <CircularProgressLoading/>
      }
      {!loadingComprovantes &&
        <div id='scroll-ref'>
          {!!comprovantes.length &&
            <div 
              tabIndex={0} 
              className="comprovante-container" 
              style={{ ...accessibility.contrastStyle, ...accessibility.contrastBorderAccessibility }}
              onKeyDown={e => { 
                if (e.key === 'Enter') { onVisualizarClicked(comprovantes[0].pdf) }
              }}
            >
              <FontAwesomeIcon icon={faFileInvoiceDollar} className='envelope-icon' style={accessibility.contrastColorAccessibility} />
              <div>
                <Typography style={accessibility.textStyleAccessibility} sx={{ ...sxTypographySubTitle, marginBottom: '15px' }}>
                  <span style={accessibility.textStyleAccessibility} className="bold">Informe de pagamentos:&nbsp;</span>
                  <span style={accessibility.textStyleAccessibility}>{comprovantes[0].exercicio}</span></Typography>
                <button
                  className='demonstrativo-button'
                  aria-label={`Visualizar comprovante do ano ${comprovantes[0].exercicio}`}
                  onClick={() => { onVisualizarClicked(comprovantes[0].pdf) }}
                  tabIndex={-1}
                  style={accessibility.textStyleAccessibility}
                >
                  Visualizar Comprovante
                  <FontAwesomeIcon icon={faArrowDown} className='arrow-down-icon' />
                </button>
              </div>
            </div>
          }
          {
            !comprovantes.length &&
            <Typography style={accessibility.textStyleAccessibility} sx={{ ...sxTypographySubTitle, marginBottom: '15px' }}>Nenhum comprovante encontrado.</Typography>
          }

          <Typography style={accessibility.textStyleAccessibility} sx={{ ...sxTypographySubTitleBold, marginTop: '40px', marginBottom: '10px' }}>Comprovantes de anos anteriores</Typography>

          {comprovantes.length > 1 &&
            <div>
              <TabelaComprovantes data={paginatedComprovantes} onVisualizarClicked={onVisualizarClicked} />
              {
                comprovantes.length - 1 > paginationLength &&
                <Pagination
                  className='pagination-holder'
                  count={Math.ceil(comprovantes.length / paginationLength)}
                  sx={accessibility.contrastMode ? sxPaginationContrast : sxPagination} hideNextButton hidePrevButton
                  color='primary'
                  page={page}
                  onChange={handlePageChange}
                />
              }
            </div>
          }
          {comprovantes.length <= 1 &&
            <Typography style={accessibility.textStyleAccessibility} sx={{ ...sxTypographySubTitle, marginBottom: '15px' }}>Nenhum comprovante encontrado.</Typography>
          }
        </div>
      }

    </div>
  )
}

export default ComprovanteRecebidos